import { useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, MessageSquare, Plus, Search, X, Wallet, CalendarClock } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import {
  formatBalance,
  useSendSms,
  useSmsClinic,
  useSmsCreditTransactions,
  useSmsLogs,
  useSmsLogsRealtime,
  useAppointmentSmsJobs,
  useTopUpSmsCredits,
  type SmsLogStatus,
  type SmsLogRow,
  type SmsLogsFilters,
  type SmsTriggerType,
  type AppointmentSmsJobRow,
} from '@/hooks/useSms';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { ApiCallError } from '@/lib/api';

const STATUSES: Array<SmsLogStatus | 'all'> = ['all', 'queued', 'sent', 'delivered', 'failed'];

export function Sms() {
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { isClinicAdmin, isPlatformAdmin } = useAuth();

  const [statusFilter, setStatusFilter] = useState<SmsLogStatus | 'all'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showSendModal, setShowSendModal] = useState(false);
  const [showTopUpModal, setShowTopUpModal] = useState(false);

  const filters = useMemo<SmsLogsFilters>(
    () => ({
      status: statusFilter,
      search: searchTerm.trim() || undefined,
    }),
    [statusFilter, searchTerm],
  );

  const clinicQuery = useSmsClinic(activeClinicId);
  const logsQuery = useSmsLogs(activeClinicId, filters);
  const txnsQuery = useSmsCreditTransactions(activeClinicId, 20);
  const jobsQuery = useAppointmentSmsJobs(activeClinicId, 50);

  // Realtime: live DLR delivery-status updates (MOD-9b)
  useSmsLogsRealtime(activeClinicId);

  const canTopUp = activeClinicId ? isPlatformAdmin || isClinicAdmin(activeClinicId) : false;

  const credits = clinicQuery.data?.credits;
  const settings = clinicQuery.data?.settings;
  const logs = logsQuery.data ?? [];
  const txns = txnsQuery.data ?? [];
  const jobs = jobsQuery.data ?? [];

  const lowBalance =
    credits != null &&
    credits.lowBalanceThreshold > 0 &&
    credits.balance <= credits.lowBalanceThreshold;

  return (
    <div className="space-y-6">
          <div className="flex flex-wrap items-center justify-end gap-2">
              <Button
                variant="primary"
                onClick={() => setShowSendModal(true)}
                disabled={!activeClinicId || !settings?.enabled}
              >
                <Plus className="mr-1 h-4 w-4" />
                {t('sms.send_sms')}
              </Button>
              {canTopUp && (
                <Button variant="secondary" onClick={() => setShowTopUpModal(true)}>
                  <Wallet className="mr-1 h-4 w-4" />
                  {t('sms.top_up')}
                </Button>
              )}
          </div>

          {/* Balance + status cards */}
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-card-dark">
              <div className="flex items-center gap-2">
                <Wallet className="h-5 w-5 text-indigo-500" />
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('sms.balance')}
                </h3>
              </div>
              <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white">
                {credits ? formatBalance(credits.balance) : '—'}
              </p>
              {lowBalance && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                  {t('sms.low_balance_warning')}
                </p>
              )}
              {credits && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {t('sms.lifetime_used')}: {formatBalance(credits.lifetimeUsed)}
                </p>
              )}
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-card-dark">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('sms.sender_label')}
              </h3>
              <p className="mt-2 truncate text-lg font-semibold text-gray-900 dark:text-white">
                {settings?.sender ?? 'Clinio'}
              </p>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {settings?.enabled ? t('sms.enabled') : t('sms.disabled')}
              </p>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-card-dark">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('sms.recent_log_count')}
              </h3>
              <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white">
                {logs.length}
              </p>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {t('sms.recent_log_hint')}
              </p>
            </div>
          </div>

          {/* Filters */}
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <div className="relative max-w-md flex-1">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('sms.search_placeholder')}
                className="block w-full rounded-md border border-gray-300 bg-white py-2 pl-10 pr-3 text-sm leading-5 placeholder-gray-500 focus:border-indigo-500 focus:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-500"
                  aria-label={t('sms.clear_search')}
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>

            <div className="inline-flex rounded-md border border-gray-300 bg-white shadow-sm dark:border-gray-600 dark:bg-gray-800">
              {STATUSES.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatusFilter(s)}
                  className={`px-3 py-1.5 text-sm first:rounded-l-md last:rounded-r-md ${
                    statusFilter === s
                      ? 'bg-indigo-600 text-white'
                      : 'text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  {t(`sms.status_${s === 'all' ? 'all' : s}`)}
                </button>
              ))}
            </div>
          </div>

          {/* Logs */}
          {logsQuery.error && (
            <div className="mt-6">
              <ErrorAlert message={t('sms.load_error')} />
            </div>
          )}

          {logsQuery.isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : logs.length === 0 ? (
            <EmptyLogs />
          ) : (
            <div className="mt-6 overflow-hidden sm:rounded-md">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <Th>{t('sms.col_when')}</Th>
                      <Th>{t('sms.col_to')}</Th>
                      <Th>{t('sms.col_trigger')}</Th>
                      <Th>{t('sms.col_status')}</Th>
                      <Th>{t('sms.col_parts')}</Th>
                      <Th>{t('sms.col_cost')}</Th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-card-dark">
                    {logs.map((log) => (
                      <LogRow key={log.id} log={log} />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* SMS Automation jobs panel (MOD-9b) */}
          <section className="mt-10">
            <div className="flex items-center gap-2">
              <CalendarClock className="h-5 w-5 text-indigo-500" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                {t('sms.automation_title')}
              </h3>
            </div>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {t('sms.automation_subtitle')}
            </p>
            {jobsQuery.isLoading ? (
              <div className="mt-4 flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('app.loading')}
              </div>
            ) : jobs.length === 0 ? (
              <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                {t('sms.automation_empty')}
              </p>
            ) : (
              <div className="mt-4 overflow-hidden sm:rounded-md">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <Th>{t('sms.col_trigger')}</Th>
                        <Th>{t('sms.col_scheduled')}</Th>
                        <Th>{t('sms.col_status')}</Th>
                        <Th>{t('sms.col_reason')}</Th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-card-dark">
                      {jobs.map((job) => (
                        <AutomationJobRow key={job.id} job={job} />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </section>

          {/* Credit ledger (admin only) */}
          {canTopUp && txns.length > 0 && (
            <section className="mt-10">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                {t('sms.ledger_title')}
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {t('sms.ledger_subtitle')}
              </p>
              <div className="mt-4 overflow-hidden sm:rounded-md">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <Th>{t('sms.col_when')}</Th>
                        <Th>{t('sms.col_kind')}</Th>
                        <Th>{t('sms.col_delta')}</Th>
                        <Th>{t('sms.col_balance_after')}</Th>
                        <Th>{t('sms.col_reason')}</Th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-card-dark">
                      {txns.map((txn) => (
                        <tr key={txn.id}>
                          <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500 dark:text-gray-400">
                            {new Date(txn.created_at).toLocaleString('da-DK')}
                          </td>
                          <td className="whitespace-nowrap px-3 py-3 text-sm">
                            <KindBadge kind={txn.kind} />
                          </td>
                          <td
                            className={`whitespace-nowrap px-3 py-3 text-sm font-medium ${
                              txn.delta_cents >= 0
                                ? 'text-green-700 dark:text-green-400'
                                : 'text-red-700 dark:text-red-400'
                            }`}
                          >
                            {txn.delta_cents >= 0 ? '+' : ''}
                            {formatBalance(txn.delta_cents)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-700 dark:text-gray-200">
                            {formatBalance(txn.balance_after_cents)}
                          </td>
                          <td className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">
                            {txn.reason ?? '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          )}

      {showSendModal && activeClinicId && (
        <SendSmsModal clinicId={activeClinicId} onClose={() => setShowSendModal(false)} />
      )}
      {showTopUpModal && activeClinicId && canTopUp && (
        <TopUpModal clinicId={activeClinicId} onClose={() => setShowTopUpModal(false)} />
      )}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      scope="col"
      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 dark:text-white sm:pl-6"
    >
      {children}
    </th>
  );
}

function StatusPill({ status }: { status: SmsLogStatus }) {
  const colors: Record<SmsLogStatus, string> = {
    queued: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
    sent: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
    delivered: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
    rejected: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
    expired: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
    unknown: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  };
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${colors[status]}`}>
      {status}
    </span>
  );
}

function KindBadge({ kind }: { kind: string }) {
  const colors: Record<string, string> = {
    topup: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    reservation: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
    refund: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
    capture: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
    adjustment: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
  };
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
        colors[kind] ?? colors.adjustment
      }`}
    >
      {kind}
    </span>
  );
}

function LogRow({ log }: { log: SmsLogRow }) {
  return (
    <tr>
      <td className="whitespace-nowrap py-3 pl-4 pr-3 text-sm text-gray-500 dark:text-gray-400 sm:pl-6">
        {new Date(log.created_at).toLocaleString('da-DK')}
      </td>
      <td className="px-3 py-3 text-sm">
        <div className="font-mono text-gray-900 dark:text-white">{log.to_phone}</div>
        {log.patient_name_snapshot && (
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {log.patient_name_snapshot}
          </div>
        )}
      </td>
      <td className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">{log.trigger}</td>
      <td className="px-3 py-3 text-sm">
        <StatusPill status={log.status} />
        {log.error_code && (
          <div className="mt-0.5 text-xs text-red-600 dark:text-red-400">{log.error_code}</div>
        )}
      </td>
      <td className="px-3 py-3 text-sm text-gray-700 dark:text-gray-200">{log.parts}</td>
      <td className="px-3 py-3 text-sm text-gray-700 dark:text-gray-200">
        {formatBalance(log.clinic_price_cents)}
      </td>
    </tr>
  );
}

function EmptyLogs() {
  const { t } = useTranslation();
  return (
    <div className="mt-6 rounded-xl border border-dashed border-gray-300 bg-white py-12 text-center dark:border-gray-600 dark:bg-card-dark">
      <MessageSquare className="mx-auto h-10 w-10 text-gray-400" />
      <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">{t('sms.empty')}</p>
    </div>
  );
}

// =============================================================================
// AutomationJobRow
// =============================================================================

function JobStatusPill({ status }: { status: AppointmentSmsJobRow['status'] }) {
  const colors: Record<AppointmentSmsJobRow['status'], string> = {
    pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
    sent: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    skipped: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  };
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${colors[status]}`}>
      {status}
    </span>
  );
}

function AutomationJobRow({ job }: { job: AppointmentSmsJobRow }) {
  return (
    <tr>
      <td className="whitespace-nowrap px-3 py-3 text-sm font-medium text-gray-900 dark:text-white">
        {job.trigger}
      </td>
      <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500 dark:text-gray-400">
        {job.scheduled_for ? new Date(job.scheduled_for).toLocaleString('da-DK') : '—'}
      </td>
      <td className="whitespace-nowrap px-3 py-3 text-sm">
        <JobStatusPill status={job.status} />
      </td>
      <td className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">{job.reason ?? '—'}</td>
    </tr>
  );
}

// =============================================================================
// SendSmsModal
// =============================================================================

function SendSmsModal({ clinicId, onClose }: { clinicId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const send = useSendSms();
  const [to, setTo] = useState('');
  const [body, setBody] = useState('');
  const [trigger, setTrigger] = useState<SmsTriggerType>('manual');

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    try {
      await send.mutateAsync({
        clinic_id: clinicId,
        to: to.trim(),
        body: body.trim(),
        trigger,
        skip_consent_check: trigger === 'test' || trigger === 'consent_request',
      });
      toast.success(t('sms.send_success'));
      onClose();
    } catch (err) {
      const msg = err instanceof ApiCallError ? err.message : t('sms.send_failed');
      toast.error(msg);
    }
  }

  const segCount = body.length === 0 ? 0 : Math.ceil(body.length / 160);

  return (
    <Modal title={t('sms.send_sms')} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label={t('sms.field_to')}
          type="tel"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          required
          placeholder="+45 12 34 56 78"
        />
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
            {t('sms.field_trigger')}
          </label>
          <select
            value={trigger}
            onChange={(e) => setTrigger(e.target.value as SmsTriggerType)}
            className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          >
            <option value="manual">manual</option>
            <option value="test">test</option>
            <option value="consent_request">consent_request</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
            {t('sms.field_body')}
          </label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={4}
            maxLength={1600}
            required
            className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {body.length} {t('sms.chars')} · {segCount} {t('sms.segments')}
          </p>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={send.isPending || to.length === 0 || body.length === 0}>
            {send.isPending ? t('common.saving') : t('sms.send_sms')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// =============================================================================
// TopUpModal
// =============================================================================

function TopUpModal({ clinicId, onClose }: { clinicId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const topUp = useTopUpSmsCredits();
  const [amount, setAmount] = useState<string>('100.00');
  const [reason, setReason] = useState('');

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const amountCents = Math.round(parseFloat(amount) * 100);
    if (!Number.isFinite(amountCents) || amountCents <= 0) {
      toast.error(t('sms.invalid_amount'));
      return;
    }
    try {
      await topUp.mutateAsync({
        clinic_id: clinicId,
        amount_cents: amountCents,
        reason: reason.trim() || undefined,
      });
      toast.success(t('sms.topup_success'));
      onClose();
    } catch (err) {
      const msg = err instanceof ApiCallError ? err.message : t('sms.topup_failed');
      toast.error(msg);
    }
  }

  return (
    <Modal title={t('sms.top_up')} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label={t('sms.field_amount_dkk')}
          type="number"
          step="0.01"
          min="0.01"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          required
        />
        <Input
          label={t('sms.field_reason')}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={t('sms.reason_placeholder')}
        />
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={topUp.isPending}>
            {topUp.isPending ? t('common.saving') : t('sms.top_up')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// =============================================================================
